import { type ReactNode, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { fontStyles } from '@/styles/typography';

import { Tooltip } from '@/ui/Tooltip';

const badgeTooltipStyles = tv({
  base: [
    'max-w-full min-w-0 items-center rounded-2xl flex justify-center',
    'outline-none bg-token-alphalight-300 hover:bg-token-alphalight-400',
    'active:bg-token-alphalight-400 focus-visible:outline-token-alphalight-500',
    'focus-visible:outline-2 focus-visible:outline-solid cursor-pointer',
  ],
  variants: {
    size: {
      sm: fontStyles({ variant: 'LABEL_XS', class: 'h-[18px] px-2 gap-0.5' }),
      md: fontStyles({ variant: 'LABEL_SM', class: 'h-6 px-2.5 gap-1.5' }),
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type BadgeTooltipProps = VariantProps<typeof badgeTooltipStyles> & {
  name: string;
  label: string;
  tooltip: string;
  icon: ReactNode;
  className?: string;
};

export const BadgeTooltip = ({
  name,
  label,
  tooltip,
  icon,
  size,
  className,
}: BadgeTooltipProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <Tooltip.Provider>
      <Tooltip.Root open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            aria-label={label}
            onClick={(event) => {
              event.preventDefault();
              setIsTooltipOpen((isOpen) => !isOpen);
            }}
            className={badgeTooltipStyles({ size, class: className })}
          >
            {icon}
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{name}</span>
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content sideOffset={8}>{tooltip}</Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
